'use client';

import React from 'react';
import { ErrorBoundary, Layout, Tabs } from '@benzinga/core-ui';
import { NoFirstRender } from '@benzinga/hooks';
import { FaBoltLightning, FaFire } from 'react-icons/fa6';

import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import type { News } from '@benzinga/basic-news-manager';
import type { Event } from '@benzinga/events-manager';
import { Quicklinks, QuickLinkItem } from '@benzinga/money';
import { ConnatixLiveBlock } from '@benzinga/blocks';
import { HomePageTabKey } from '../../../src/components/Home/interface';
import { pageTabs } from '../../../src/components/Home/components/homeTabConstants';
import { MarketPulseChart } from '../../_components/markets';
import { LatestNewsSideWidget } from './LatestNewsSideWidget';
import { NewsLayout } from './NewsLayout';

const tabs = Object.entries(pageTabs).map(([key, tab]) => ({
  key,
  name: tab.title,
  link: tab.url,
}));

const NativoAdPlaceholder = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.NativoAdPlaceholder };
  }),
);

const FloatingWNSTNWidget = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FloatingWNSTNWidget })),
);

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

const WatchlistCTABanner = React.lazy(() =>
  import('@benzinga/watchlist-ui').then(module => {
    return { default: module.WatchlistCTABanner };
  }),
);

const WatchlistWidget = React.lazy(() =>
  import('./WatchlistWidget').then(module => ({ default: module.WatchlistWidget })),
);

const InsiderTradesWidget = React.lazy(() =>
  import('./InsiderTradesWidget').then(module => ({ default: module.InsiderTradesWidget })),
);

const CalendarInsightsWidget = React.lazy(() =>
  import('./CalendarInsightsWidget').then(module => ({ default: module.CalendarInsightsWidget })),
);

const EventsSideWidget = React.lazy(() =>
  import('./EventsSideWidget').then(module => ({ default: module.EventsSideWidget })),
);

const ExpertIdeasNewsSideWidget = React.lazy(() =>
  import('./ExpertIdeasNewsSideWidget').then(module => ({ default: module.ExpertIdeasNewsSideWidget })),
);

interface LayoutMainProps {
  activeTab: HomePageTabKey;
  markets: DelayedQuote[];
  quicklinks: QuickLinkItem[];
  latestNews: StoryObject[];
  expertIdeas: StoryObject[];
  wiimNews: StoryObject[];
  exclusivesNews: StoryObject[];
  sponsoredNews: News[];
  chainwireNews: StoryObject[];
  trendingNews: News[];
  topStoriesNews: News[];
  events: Event[];
  tabContent?: React.ReactNode;
  pageTitle?: string;
}

export const LayoutMain: React.FC<LayoutMainProps> = ({
  activeTab,
  latestNews,
  markets,
  expertIdeas,
  wiimNews,
  exclusivesNews,
  sponsoredNews,
  chainwireNews,
  topStoriesNews,
  trendingNews,
  quicklinks,
  events,
  tabContent,
  pageTitle,
}: LayoutMainProps) => {
  const showTabHeader = !!tabs.find(tab => tab.key === activeTab);

  const defaultContent = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col-reverse lg:flex-row gap-4">
        <div className="w-full lg:max-w-[300px]">
          <LatestNewsSideWidget news={latestNews} />
        </div>
        <div className="w-full flex flex-col">
          <NewsLayout
            news={topStoriesNews as StoryObject[]}
            layout="vertical-hero-grid"
            title="Top Stories"
            variant="dark"
            highPriority={true}
            fillHeight={true}
            showTicker={false}
          />
        </div>
      </div>
      <Quicklinks quicklinks={quicklinks} />
      <MarketPulseChart
        markets={markets}
        variant="compact"
        showTickerNames={true}
        icon={
          <div className="flex items-center justify-center w-6 h-6 bg-bzgray-800 rounded-full">
            <FaBoltLightning size={14} className="text-yellow-300 rotate-12" />
          </div>
        }
      />
      <NewsLayout
        news={sponsoredNews as StoryObject[]}
        layout="horizontal-cards"
        title="Sponsored Content"
        actionLabel="See More"
        actionLink="/topic/partner-content"
        variant="default"
        showTicker={false}
        sponsored={true}
      />
      <WatchlistCTABanner />
      <NewsLayout
        news={wiimNews}
        layout="vertical-hero-grid"
        title="Why Is It Moving?™"
        actionLabel="See More"
        actionLink="/topic/why-its-moving"
        variant="default"
        showTicker={false}
      />
      <NewsLayout
        news={exclusivesNews}
        layout="horizontal-cards"
        title="Exclusives"
        actionLabel="See More"
        actionLink="/exclusives"
        variant="default"
        showTicker={false}
      />
      <NewsLayout
        news={trendingNews as StoryObject[]}
        layout="horizontal-split"
        title="Trending Stories"
        variant="dark"
        showTicker={false}
        icon={
          <div className="flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
            <FaFire size={14} className="text-white" />
          </div>
        }
      />
      <CalendarInsightsWidget />
      <NewsLayout
        news={chainwireNews}
        layout="horizontal-carousel"
        title="Chainwire"
        subtitle="(CRYPTOCURRENCY PRESS RELEASES BROUGHT TO YOU BY CHAINWIRE)"
        variant="default"
        showTicker={false}
      />
    </div>
  );

  return (
    <div className="homepage-layout-main">
      {showTabHeader && (
        <div className="w-full flex items-center justify-center px-2 tab-header">
          <div className="layout-container max-w-[1400px] w-full">
            <Tabs activeTab={activeTab} isShowMoreButtonEnabled={false} tabBarGutter={4} tabs={tabs} variant="lifted" />
          </div>
        </div>
      )}
      <Layout
        layoutMain={<>{tabContent ?? defaultContent}</>}
        layoutSidebar={
          <div className="flex flex-col gap-4 w-full">
            <React.Suspense fallback={<div className="min-h-[250px] w-[300px]" />}>
              <FreestarAd className="w-[300px] overflow-hidden" placement="sidebar_1" />
            </React.Suspense>
            <ExpertIdeasNewsSideWidget news={expertIdeas} />
            <ConnatixLiveBlock fallback={<WatchlistWidget />} />
            <EventsSideWidget events={events} />
            <NativoAdPlaceholder type="sidebar" />
            <React.Suspense fallback={<div className="min-h-[250px] w-[300px]" />}>
              <FreestarAd className="w-[300px]" placement="sidebar_2" />
            </React.Suspense>
            <InsiderTradesWidget />
          </div>
        }
        title={pageTitle ?? ''}
      />
      <ErrorBoundary name="home-page-floating-wnstn-widget">
        <React.Suspense fallback={<div />}>
          <NoFirstRender>
            <FloatingWNSTNWidget />
          </NoFirstRender>
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default LayoutMain;
